/* Aanmaken, dupliceren, archiveren en terugzetten van een Businesscase (ADR 0023).
 *
 * Aanmaken gebeurt alleen nog op een uitdrukkelijke klik. De oude route
 * /crm/projects/:id/businesscase maakte stil een lege "Businesscase v1" aan als het
 * project er nog geen had; zo ontstonden de lege cases van P17, P23, P28 en P39.
 *
 * Er wordt niets echt verwijderd: archiveren zet is_archived, terugzetten haalt het weg.
 *
 * Relatieve imports (geen @/), zodat de toetsen dit bestand zonder bundel lezen. */

import type { BcClient } from './lijst';

/** De tien jaren die een nieuwe case krijgt, vanaf dit jaar (niet vóór 2025). */
export function beginJaren(nu: Date = new Date()): number[] {
  const start = Math.max(2025, nu.getFullYear());
  return Array.from({ length: 10 }, (_, i) => start + i);
}

export async function maakBusinesscase(
  client: BcClient,
  v: { projectId: string; naam: string; notities?: string | null; gebruiker: string | null },
  nu: Date = new Date(),
): Promise<string> {
  const naam = v.naam.trim();
  if (!naam) throw new Error('Geef de businesscase een naam.');
  const { data, error } = await client
    .from('businesscases')
    .insert({ project_id: v.projectId, name: naam, notes: v.notities?.trim() || null, created_by: v.gebruiker })
    .select('id')
    .single();
  if (error) throw error;
  const jaren = beginJaren(nu).map((year) => ({ businesscase_id: data.id, year }));
  const r = await client.from('businesscase_revenues').insert(jaren);
  if (r.error) throw r.error;
  return data.id as string;
}

/* Wat een kopie meeneemt. Tot 29-09-2026 vielen flex_e_value, roi_calculation_mode en de
   extra kosten weg, zodat een kopie andere cijfers gaf dan het origineel. */
const KOPIEER = [
  'project_id', 'notes', 'battery_purchase_cost', 'installation_cost', 'software_setup_cost', 'annual_subscription',
  'other_costs', 'eia_enabled', 'flex_e_enabled', 'flex_e_value', 'other_subsidy', 'tax_rate', 'roi_calculation_mode',
] as const;
const OPBRENGST = [
  'year', 'grid_connection_savings', 'grid_purchase_cost_savings', 'dayahead_incomes', 'hbe_incomes', 'energy_tax_savings',
  'endex_cost_savings', 'imbalance_income', 'imbalance_discount', 'ems_costs', 'total_opex', 'generator_cost_savings',
  'extra_sde', 'total_solar_revenues',
] as const;

export async function dupliceerBusinesscase(client: BcClient, bronId: string, naam: string, gebruiker: string | null): Promise<string> {
  const bron = await client.from('businesscases').select(['id', 'version', ...KOPIEER].join(', ')).eq('id', bronId).single();
  if (bron.error) throw bron.error;
  const nieuw = await client
    .from('businesscases')
    .insert({
      ...Object.fromEntries(KOPIEER.map((k) => [k, bron.data[k]])),
      name: naam.trim() || 'Kopie',
      version: (Number(bron.data.version) || 1) + 1,
      created_by: gebruiker,
    })
    .select('id')
    .single();
  if (nieuw.error) throw nieuw.error;
  const id = nieuw.data.id as string;

  const [opb, extra] = await Promise.all([
    client.from('businesscase_revenues').select(OPBRENGST.join(', ')).eq('businesscase_id', bronId),
    client.from('businesscase_extra_costs').select('name, amount').eq('businesscase_id', bronId),
  ]);
  if (opb.error) throw opb.error;
  if (extra.error) throw extra.error;
  if (opb.data?.length) {
    const r = await client.from('businesscase_revenues').insert(opb.data.map((o: any) => ({ ...o, businesscase_id: id })));
    if (r.error) throw r.error;
  }
  if (extra.data?.length) {
    const r = await client.from('businesscase_extra_costs').insert(extra.data.map((e: any) => ({ ...e, businesscase_id: id })));
    if (r.error) throw r.error;
  }
  return id;
}

async function zetGearchiveerd(client: BcClient, id: string, aan: boolean): Promise<void> {
  const { data, error } = await client
    .from('businesscases')
    .update({ is_archived: aan, archived_at: aan ? new Date().toISOString() : null })
    .eq('id', id)
    .eq('is_archived', !aan)
    .select('id');
  if (error) throw error;
  if (!data?.length) {
    throw new Error(aan
      ? 'Deze businesscase is al gearchiveerd, of je mag hem niet wijzigen. Laad de pagina opnieuw.'
      : 'Deze businesscase is niet gearchiveerd, of je mag hem niet wijzigen. Laad de pagina opnieuw.');
  }
}

export const archiveerBusinesscase = (client: BcClient, id: string) => zetGearchiveerd(client, id, true);
export const herstelBusinesscase = (client: BcClient, id: string) => zetGearchiveerd(client, id, false);
